import React, { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { TooltipComponent } from "../../../components/Component";
import { Button } from "reactstrap";
import {
  Badge,
  Button as AntButton,
  Popconfirm,
  Progress,
  Table,
  Tooltip,
} from "antd";
import {
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import SearchInput from "../../form-components/SearchComponent";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import { useLocation, useNavigate } from "react-router";
import TopMenu from "../../../route/topMenu";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { projectsApis } from "../../../api/projects";
import ProjectInDetailModal from "./ProjectInDetailModal";
import ProjectPageShell from "../shared/ProjectPageShell";

interface UserData {
  userDetailId?: number;
  status?: string;
  ownerName?: string;
}

const ProjectUsersListView: FC<{}> = () => {
  const location = useLocation();
  const { id, derivedProjectStatus, projectName, extra } = location.state || {};
  const dispatch = useDispatch();
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );

  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState<UserData | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  const handleUserPreview = (userDetailId: number, userName: string) => {
    setModalData({
      // ...record,
      userDetailId: userDetailId,
      ownerName: userName,
      status: "Active",
    });
    setActiveTab("overview");
    setModalVisible(true);
  };

  const columns: any = [
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
      width: 200,
      fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`user-${record?.userId}`}
              onClick={() => handleUserPreview(record.userDetailId, text)}
            >
              {text}
            </span>
            {/* ... */}
          </>
        );
      },
    },
    {
      title: "Department",
      dataIndex: "departmentName",
      key: "departmentName",
      width: 200,
    },
    {
      title: "Role",
      dataIndex: "roleName",
      key: "roleName",
      width: 200,
    },
    {
      title: "Reporting Manager",
      dataIndex: "reportingManagerName",
      key: "reportingManagerName",
      width: 200,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Phone Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: 150,
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 150,
      render: (text: string) => dayjs(text).format("DD-MMM-YYYY"),
    },
  ];

  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setsearchQuery] = useState(null);
  const [filtersModal, setFiltersModal] = useState(false);
  const [selectFilter1, setselectFilter1] = useState<number[] | null>(null);
  const [selectFilter2, setselectFilter2] = useState(null);
  const [selectFilter3, setselectFilter3] = useState(null);
  const [filterCount, setFilterCount] = useState(0);
  const [roleOptions, setRoleOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [endOfList, setEndOfList] = useState(false);

  const fetchRoleOptions = async () => {
    try {
      const response = await projectsApis.fetchProjectRolesWithUsers(id);
      if (response?.status) {
        const seen = new Set<number>();
        const opts = (response.data || [])
          .map((r: any) => ({
            value: r.roleId,
            label: r.roleName,
          }))
          .filter((opt: { value: number; label: string }) => {
            if (opt.value == null || seen.has(opt.value)) return false;
            seen.add(opt.value);
            return true;
          });
        setRoleOptions(opts);
      }
    } catch (error) {
      console.error("Error loading role options:", error);
    }
  };

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        pageSize,
        selectFilter1,
      } = params;

      const queryParams: any = {};
      if (selectFilter1?.length) {
        queryParams.roleIds = selectFilter1.join(",");
      }

      const apiResponse = await projectsApis.fetchProjectUsers(
        id,
        Object.keys(queryParams).length ? queryParams : undefined
      );

      const { data, status } = apiResponse;
      if (status) {
        if (data.length < pageSize) {
          setEndOfList(true);
        }

        setDataSource((prev: any) => (append ? [...prev, ...data] : data));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1;
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current ? "F" : "B";
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId
            : dataSource[0]?.paginationId;

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
      selectFilter1,
      selectFilter2,
      selectFilter3,
    });
  };

  const refreshListView = () => {
    try {
      setEndOfList(false);
      setPagination({
        current: 1,
        pageSize: 15,
        defaultCurrent: 1,
        defaultPageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ["15", "20", "30", "40", "50"],
        total: 0,
      });
      refreshData({
        pageDiff: 0,
      });
    } catch (error) {}
  };

  useEffect(() => {
    refreshListView();
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location]);

  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      dateRange: "Quarter to Date",
      ...overRideData,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  const toolbar = (
    <>
      <FormProvider {...methods}>
        <form>
          <div style={{ width: "300px" }}>
            <SearchInput
              name="search"
              placeholder="Search..."
              width="100%"
              prefixIcon={<SearchOutlined />}
              onDebouncedSearch={async (value: any) => {
                setsearchQuery(value);
                setPagination({
                  ...pagination,
                  current: 1,
                });
                await fetchData({
                  oldPage: 1,
                  current: 1,
                  sortOrder: "DESC",
                  pageSize: pagination.pageSize.toString(),
                  direction: "P",
                  pageDiff: 0,
                  paginationId: null,
                  searchQuery: value,
                  selectFilter1,
                  selectFilter2,
                  selectFilter3,
                });
              }}
              debounceDelay={300}
            />
          </div>
        </form>
      </FormProvider>
      <span className="pms-toolbar-spacer" />
      <AntButton
        type="text"
        icon={<ReloadOutlined />}
        title="Refresh"
        onClick={() => refreshListView()}
      />
      <Popconfirm
                          title={<></>}
                          icon={<></>}
                          placement="topRight"
                          showCancel={false}
                          open={filtersModal}
                          okButtonProps={{
                            style: {
                              display: "none",
                            },
                          }}
                          onConfirm={() => {
                            try {
                              console.log("confirm");
                            } catch (error) {
                              console.log(error);
                            }
                          }}
                          onCancel={() => {
                            alert("cancel");
                          }}
                          onOpenChange={(visible) => {
                            if (visible) {
                              setFiltersModal(visible);
                              if (!roleOptions.length) {
                                fetchRoleOptions();
                              }
                            }
                          }}
                          description={
                            <div className="" style={{ width: "300px" }}>
                              <div className="d-flex align-items-center flex-wrap w-100">
                                <FormProvider {...methods}>
                                  <form
                                    onSubmit={methods.handleSubmit((data) => {
                                      const roleIds: number[] = data?.role || [];
                                      setselectFilter1(
                                        roleIds.length ? roleIds : null
                                      );
                                      setFilterCount(roleIds.length);
                                      setFiltersModal(false);
                                      setPagination((prev) => ({
                                        ...prev,
                                        current: 1,
                                      }));
                                      fetchData({
                                        oldPage: 1,
                                        current: 1,
                                        sortOrder: "DESC",
                                        pageSize: pagination.pageSize.toString(),
                                        direction: "P",
                                        pageDiff: 0,
                                        paginationId: null,
                                        searchQuery,
                                        selectFilter1: roleIds.length
                                          ? roleIds
                                          : null,
                                        selectFilter2,
                                        selectFilter3,
                                      });
                                    })}
                                    className="w-100"
                                  >
                                    <div
                                      key={"filters"}
                                      className="mt-1 mb-0"
                                      style={{ marginRight: 10 }}
                                    >
                                      <FormMultiSelect
                                        name={`role`}
                                        label={"Role"}
                                        placeholder={"Filter By Role"}
                                        options={roleOptions}
                                      />
                                    </div>

                                    <div className="actions clearfix">
                                      <ul>
                                        <li>
                                          <Button
                                            color="primary"
                                            size="sm"
                                            type="submit"
                                          >
                                            Save
                                          </Button>
                                        </li>
                                        <li>
                                          <Button
                                            size="sm"
                                            color="primary"
                                            className="btn-dim btn btn-secondary"
                                            type="button"
                                            onClick={() => {
                                              const hadFilter = !!selectFilter1;
                                              setFiltersModal(false);
                                              methods.reset({ role: [] });
                                              setselectFilter1(null);
                                              setFilterCount(0);
                                              if (hadFilter) {
                                                setPagination((prev) => ({
                                                  ...prev,
                                                  current: 1,
                                                }));
                                                fetchData({
                                                  oldPage: 1,
                                                  current: 1,
                                                  sortOrder: "DESC",
                                                  pageSize:
                                                    pagination.pageSize.toString(),
                                                  direction: "P",
                                                  pageDiff: 0,
                                                  paginationId: null,
                                                  searchQuery,
                                                  selectFilter1: null,
                                                  selectFilter2,
                                                  selectFilter3,
                                                });
                                              }
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        </li>
                                      </ul>
                                    </div>
                                  </form>
                                </FormProvider>
                              </div>
                            </div>
                          }
                        >
        <Badge color="geekblue" size="small" count={filterCount}>
          <AntButton type="text" icon={<FilterOutlined />} />
        </Badge>
      </Popconfirm>
    </>
  );

  return (
    <>
      <ProjectPageShell
        headTitle="Project Users"
        breadcrumb={[{ label: projectName }, { label: "Project Users" }]}
        toolbar={toolbar}
      >
        <SkeletonTable
          loading={loading}
          columns={columns as SkeletonTableColumnsType[]}
        >
          <Table
            rowKey="userId"
            className="pms-hub-table"
            columns={columns}
            dataSource={dataSource}
            pagination={pagination}
            onChange={tableOnChange}
            size="middle"
            scroll={{ x: 1400 }}
          />
        </SkeletonTable>
      </ProjectPageShell>

      {/* Modal component with local state */}
      {modalVisible && modalData && (
        <ProjectInDetailModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          activeTab={activeTab}
          userData={modalData}
        />
      )}
    </>
  );
};

export default ProjectUsersListView;
